class ProductCardGallery extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.isInitialized = false;
    this.didSwipe = false;
    this.imagesPreloaded = false;
    this.touchStartX = null;
    this.touchStartY = null;
    this.swipeThreshold = 40;
    this.animate = true;
  }

  connectedCallback() {
    if (this.isInitialized || !this.hasAttribute('data-gallery')) return;

    this.track = this.querySelector('.card__gallery-track');
    this.slides = Array.from(this.querySelectorAll('.card__gallery-slide'));
    this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    this.prevButton = this.querySelector('[data-gallery-prev]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.loop = this.getAttribute('data-loop') === 'true';
    this.animate = this.getAttribute('data-animation') === 'true';
    this.cardLink = this.closest('[data-card-link]');
    this.cardWrapper = this.closest('.card-wrapper') || this;

    if (!this.track || this.slides.length < 2) return;

    this.bindEvents();
    this.goTo(0, false);
    this.isInitialized = true;
  }

  disconnectedCallback() {
    this.unbindEvents();
    this.isInitialized = false;
  }

  /**
  * Attaches navigation, preload, and gesture listeners.
  */
  bindEvents() {
    this.onPrevClick = this.onArrowClick.bind(this, -1);
    this.onNextClick = this.onArrowClick.bind(this, 1);
    this.onTouchStart = this.handleTouchStart.bind(this);
    this.onTouchMove = this.handleTouchMove.bind(this);
    this.onTouchEnd = this.handleTouchEnd.bind(this);
    this.onLinkClick = this.handleLinkClick.bind(this);
    this.onStopNav = this.stopLinkNavigation.bind(this);
    this.onPreload = this.preloadAllImages.bind(this);
    this.onDotClick = this.handleDotClick.bind(this);

    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.onPrevClick);
      this.prevButton.addEventListener('mousedown', this.onStopNav);
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.onNextClick);
      this.nextButton.addEventListener('mousedown', this.onStopNav);
    }

    this.dots.forEach((dot) => {
      dot.addEventListener('click', this.onDotClick);
      dot.addEventListener('mousedown', this.onStopNav);
    });

    this.cardWrapper.addEventListener('mouseenter', this.onPreload, { once: true });
    this.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.addEventListener('touchmove', this.onTouchMove, { passive: true });
    this.addEventListener('touchend', this.onTouchEnd, { passive: true });

    if (this.cardLink) {
      this.cardLink.addEventListener('click', this.onLinkClick);
    }
  }

  /**
  * Removes navigation and gesture listeners.
  */
  unbindEvents() {
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.onPrevClick);
      this.prevButton.removeEventListener('mousedown', this.onStopNav);
    }

    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.onNextClick);
      this.nextButton.removeEventListener('mousedown', this.onStopNav);
    }

    if (this.dots) {
      this.dots.forEach((dot) => {
        dot.removeEventListener('click', this.onDotClick);
        dot.removeEventListener('mousedown', this.onStopNav);
      });
    }

    if (this.cardWrapper && this.onPreload) {
      this.cardWrapper.removeEventListener('mouseenter', this.onPreload);
    }

    this.removeEventListener('touchstart', this.onTouchStart);
    this.removeEventListener('touchmove', this.onTouchMove);
    this.removeEventListener('touchend', this.onTouchEnd);

    if (this.cardLink) {
      this.cardLink.removeEventListener('click', this.onLinkClick);
    }
  }

  /**
  * Starts loading every gallery image (non-blocking).
  * Desktop: first mouseenter on the card. Mobile: first touchstart on the gallery.
  */
  preloadAllImages() {
    if (this.imagesPreloaded) return;

    this.slides.forEach(function (slide) {
      const img = slide.querySelector('img');
      if (!img) return;

      if (img.loading === 'lazy') {
        img.loading = 'eager';
      }

      if (typeof img.decode === 'function') {
        img.decode().catch(function () {});
      }
    });

    this.imagesPreloaded = true;
  }

  /**
  * Prevents the wrapping product link from navigating.
  * @param {Event} event
  */
  stopLinkNavigation(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
  * Handles arrow clicks without opening the PDP.
  * @param {number} direction
  * @param {MouseEvent} event
  */
  onArrowClick(direction, event) {
    this.stopLinkNavigation(event);
    this.preloadAllImages();
    this.goTo(this.currentIndex + direction);
  }

  /**
  * Jumps to the slide matching the clicked pagination dot.
  * @param {MouseEvent} event
  */
  handleDotClick(event) {
    this.stopLinkNavigation(event);
    this.preloadAllImages();

    const dot = event.currentTarget;
    const index = parseInt(dot.getAttribute('data-gallery-dot'), 10);

    if (Number.isNaN(index)) return;

    this.goTo(index);
  }

  /**
  * Blocks PDP navigation after a horizontal swipe on the gallery.
  * @param {MouseEvent} event
  */
  handleLinkClick(event) {
    if (!this.didSwipe) return;
    event.preventDefault();
    event.stopPropagation();
    this.didSwipe = false;
  }

  /**
  * @param {TouchEvent} event
  */
  handleTouchStart(event) {
    this.preloadAllImages();
    if (!event.changedTouches || !event.changedTouches[0]) return;
    this.touchStartX = event.changedTouches[0].clientX;
    this.touchStartY = event.changedTouches[0].clientY;
    this.didSwipe = false;
  }

  /**
  * @param {TouchEvent} event
  */
  handleTouchMove(event) {
    if (this.touchStartX === null || !event.changedTouches || !event.changedTouches[0]) return;

    const deltaX = event.changedTouches[0].clientX - this.touchStartX;
    const deltaY = event.changedTouches[0].clientY - this.touchStartY;

    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > this.swipeThreshold) {
      this.didSwipe = true;
    }
  }

  /**
  * @param {TouchEvent} event
  */
  handleTouchEnd(event) {
    if (this.touchStartX === null || !event.changedTouches || !event.changedTouches[0]) return;

    const deltaX = event.changedTouches[0].clientX - this.touchStartX;
    const deltaY = event.changedTouches[0].clientY - this.touchStartY;
    this.touchStartX = null;
    this.touchStartY = null;

    if (Math.abs(deltaX) < this.swipeThreshold || Math.abs(deltaX) < Math.abs(deltaY)) {
      return;
    }

    this.didSwipe = true;
    this.goTo(this.currentIndex + (deltaX < 0 ? 1 : -1));
  }

  /**
  * Syncs pagination dots with the active slide.
  * @param {number} activeIndex
  */
  updateDots(activeIndex) {
    if (!this.dots || !this.dots.length) return;

    this.dots.forEach(function (dot, dotIndex) {
      const isActive = dotIndex === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  /**
  * Moves the gallery to a slide index immediately.
  * @param {number} index
  * @param {boolean} [animate]
  */
  goTo(index, animate) {
    if (animate === undefined) animate = this.animate;

    const total = this.slides.length;
    let nextIndex = index;

    if (this.loop) {
      nextIndex = ((index % total) + total) % total;
    } else {
      nextIndex = Math.max(0, Math.min(index, total - 1));
    }

    this.currentIndex = nextIndex;

    if (!animate) {
      this.track.style.transition = 'none';
    } else {
      this.track.style.transition = '';
    }

    this.track.style.transform = 'translate3d(-' + nextIndex * 100 + '%, 0, 0)';

    this.slides.forEach(function (slide, slideIndex) {
      slide.setAttribute('aria-hidden', slideIndex === nextIndex ? 'false' : 'true');
    });

    this.updateDots(nextIndex);
    this.updateArrowState();

    if (!animate) {
      void this.track.offsetHeight;
      this.track.style.transition = '';
    }
  }

  /**
  * Enables/disables arrows when loop is off.
  */
  updateArrowState() {
    if (this.loop || !this.prevButton || !this.nextButton) return;

    const atStart = this.currentIndex === 0;
    const atEnd = this.currentIndex === this.slides.length - 1;

    this.prevButton.disabled = atStart;
    this.nextButton.disabled = atEnd;
    this.prevButton.setAttribute('aria-disabled', String(atStart));
    this.nextButton.setAttribute('aria-disabled', String(atEnd));
  }
}

customElements.define('product-card-gallery', ProductCardGallery);
